import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import Reveal from "./Reveal";
import { useCart } from "@/context/CartContext";

export function ProductCard({ p, index = 0 }) {
  const { add } = useCart();
  const spec = p.specs || {};
  const chips = [spec.Material, spec.MOQ].filter(Boolean).slice(0, 2);

  return (
    <div
      data-testid={`product-card-${p.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-mint/15 bg-moss/30 hover:border-mint/40 transition-colors clip-corner"
    >
      <Link to={`/product/${p.slug}`} className="relative block aspect-[4/3] overflow-hidden">
        <img
          src={p.image}
          alt={p.name}
          loading={index < 4 ? "eager" : "lazy"}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-transparent to-transparent opacity-70" />
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(244,237,226,0.18), transparent 55%)" }}
        />
        <span className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.25em] uppercase bg-forest/70 backdrop-blur-sm border border-mint/20 text-mintsoft px-2.5 py-1 rounded-full">
          {p.category}
        </span>
      </Link>

      <div className="flex flex-col gap-3 p-5 grow">
        <div>
          <Link to={`/product/${p.slug}`}>
            <h3 className="font-display font-medium text-xl text-kraft leading-snug hover:text-mint transition-colors">
              {p.name}
            </h3>
          </Link>
          <p className="mt-1 text-sm text-kraft/55">{p.tagline}</p>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-auto">
          {chips.map((c, i) => (
            <span key={i} className="font-mono text-[9px] tracking-[0.15em] uppercase text-mintsoft/80 border border-mint/20 rounded-full px-2 py-0.5">
              {c}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 pt-2">
          <Link
            to={`/product/${p.slug}`}
            data-testid={`product-view-${p.slug}`}
            className="font-mono text-[10px] tracking-[0.2em] uppercase text-kraft/70 hover:text-mint transition-colors"
          >
            Details →
          </Link>
          <button
            data-testid={`add-to-quote-${p.slug}`}
            onClick={() => add(p, 1, "")}
            className="inline-flex items-center gap-1.5 rounded-full bg-mint text-forest text-xs font-semibold px-4 h-9 hover:bg-kraft transition-colors"
          >
            <Plus size={14} /> Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Featured({ products }) {
  const featured = products.slice(0, 4);

  return (
    <section className="relative py-24 sm:py-28 bg-moss/20 border-y border-mint/10" data-testid="featured-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-mintsoft/90 mb-4">
            02 — The Line-Up
          </p>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <h2 className="font-display font-semibold tracking-tight text-3xl sm:text-5xl text-kraft">
              Boxes we fold every day
            </h2>
            <Link
              to="/shop"
              data-testid="featured-view-all"
              className="font-mono text-[11px] tracking-[0.2em] uppercase text-mint hover:text-kraft transition-colors"
            >
              View all →
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.07} className="h-full">
              <ProductCard p={p} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
